import { MemoryClient } from "@cloudbase/agent-agents";
import { BaseStore } from "@langchain/core/stores";

/**
 * Configuration options for TDAIStore
 */
export interface TDAIStoreInput {
  /**
   * The TDAI Memory Client instance
   */
  client: MemoryClient;

  /**
   * The amount of keys to retrieve per batch when yielding keys.
   * @default 1000
   */
  yieldKeysScanBatchSize?: number;

  /**
   * The namespace to use for the keys in the database.
   */
  namespace?: string;

  /**
   * Default session ID for storing records
   */
  defaultSessionId?: string;

  /**
   * Default strategy for storing records
   */
  defaultStrategy?: string;

  /**
   * TTL for records in seconds
   */
  ttlSeconds?: number;
}

/**
 * TDAI implementation of the BaseStore for key-value caching.
 * Uses TDAI Memory Client for persistent storage.
 *
 * @example
 * ```typescript
 * const client = new MemoryClient({
 *   endpoint: "https://memory.tdai.tencentyun.com",
 *   apiKey: "your-api-key",
 *   memoryId: "your-memory-id",
 * });
 *
 * const store = new TDAIStore({
 *   client,
 *   namespace: "cache",
 * });
 *
 * const encoder = new TextEncoder();
 * await store.mset([
 *   ["key1", encoder.encode("value1")],
 *   ["key2", encoder.encode("value2")],
 * ]);
 *
 * const values = await store.mget(["key1", "key2"]);
 * ```
 */
export class TDAIStore extends BaseStore<string, Uint8Array> {
  lc_namespace = ["langchain", "storage", "tdai"];

  protected client: MemoryClient;
  protected namespace?: string;
  protected yieldKeysScanBatchSize = 1000;
  protected defaultSessionId: string;
  protected defaultStrategy: string;
  protected ttlSeconds?: number;
  private sessionCache?: string;

  constructor(fields: TDAIStoreInput) {
    super();
    this.client = fields.client;
    this.namespace = fields.namespace;
    this.yieldKeysScanBatchSize =
      fields.yieldKeysScanBatchSize ?? this.yieldKeysScanBatchSize;
    this.defaultSessionId =
      fields.defaultSessionId ?? "langchain_store_session";
    this.defaultStrategy = fields.defaultStrategy ?? "kv_cache";
    this.ttlSeconds = fields.ttlSeconds;
  }

  /**
   * Get prefixed key with namespace
   */
  private _getPrefixedKey(key: string): string {
    if (this.namespace) {
      const delimiter = "/";
      return `${this.namespace}${delimiter}${key}`;
    }
    return key;
  }

  /**
   * Remove prefix from key
   */
  private _getDeprefixedKey(key: string): string {
    if (this.namespace) {
      const delimiter = "/";
      return key.slice(this.namespace.length + delimiter.length);
    }
    return key;
  }

  /**
   * Get or create session for the store
   */
  private async _getSession(): Promise<string> {
    if (this.sessionCache) {
      return this.sessionCache;
    }

    try {
      // Try to find existing session
      const sessions = await this.client.querySessions({
        limit: 1,
      });

      if (sessions.sessions && sessions.sessions.length > 0) {
        this.sessionCache = sessions.sessions[0].sessionId;
      } else {
        // Create new session
        const result = await this.client.createSession({
          name: `langchain_store_${this.namespace || "default"}`,
        });
        this.sessionCache = result.sessionId;
      }

      return this.sessionCache!;
    } catch (error) {
      // Fallback to default session
      return this.defaultSessionId;
    }
  }

  /**
   * Create record content for storage
   */
  private _createRecordContent(key: string, value: Uint8Array): string {
    const decoder = new TextDecoder();
    const content = {
      key: this._getPrefixedKey(key),
      value: decoder.decode(value),
      timestamp: Date.now(),
      ttl: this.ttlSeconds,
    };
    return JSON.stringify(content);
  }

  /**
   * Parse record content from storage
   */
  private _parseRecordContent(
    content: string
  ): {
    key: string;
    value: Uint8Array;
    timestamp: number;
    ttl?: number;
  } | null {
    try {
      const data = JSON.parse(content);
      const encoder = new TextEncoder();

      // Check TTL if present
      if (data.ttl && data.timestamp) {
        const now = Date.now();
        if (now > data.timestamp + data.ttl * 1000) {
          return null; // Expired
        }
      }

      return {
        key: data.key,
        value: encoder.encode(data.value),
        timestamp: data.timestamp,
        ttl: data.ttl,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Gets multiple keys from the TDAI store.
   * @param keys Array of keys to be retrieved.
   * @returns An array of retrieved values.
   */
  async mget(keys: string[]): Promise<(Uint8Array | undefined)[]> {
    if (keys.length === 0) {
      return [];
    }

    try {
      const sessionId = await this._getSession();
      const results: (Uint8Array | undefined)[] = new Array(keys.length);

      // Search for each key individually due to TDAI API limitations
      for (let i = 0; i < keys.length; i++) {
        const prefixedKey = this._getPrefixedKey(keys[i]);

        try {
          const searchResult = await this.client.searchRecords({
            content: prefixedKey,
            sessionId,
            strategies: [this.defaultStrategy],
            limit: 1,
          });

          if (searchResult.records && searchResult.records.length > 0) {
            const record = searchResult.records[0];
            const parsed = this._parseRecordContent(record.record_content);

            if (parsed && parsed.key === prefixedKey) {
              results[i] = parsed.value;
            } else {
              results[i] = undefined;
            }
          } else {
            results[i] = undefined;
          }
        } catch (error) {
          results[i] = undefined;
        }
      }

      return results;
    } catch (error) {
      // Return array of undefined values on error
      return new Array(keys.length).fill(undefined);
    }
  }

  /**
   * Sets multiple keys in the TDAI store.
   * @param keyValuePairs Array of key-value pairs to be set.
   * @returns Promise that resolves when all keys have been set.
   */
  async mset(keyValuePairs: [string, Uint8Array][]): Promise<void> {
    if (keyValuePairs.length === 0) {
      return;
    }

    try {
      const sessionId = await this._getSession();

      // Set each key-value pair
      for (const [key, value] of keyValuePairs) {
        const content = this._createRecordContent(key, value);

        try {
          await this.client.appendRecord({
            sessionId,
            content,
            strategy: this.defaultStrategy,
          });
        } catch (error) {
          console.warn(`Failed to set key ${key}:`, error);
        }
      }
    } catch (error) {
      throw new Error(`Failed to set keys: ${error}`);
    }
  }

  /**
   * Deletes multiple keys from the TDAI store.
   * @param keys Array of keys to be deleted.
   * @returns Promise that resolves when all keys have been deleted.
   */
  async mdelete(keys: string[]): Promise<void> {
    if (keys.length === 0) {
      return;
    }

    try {
      const sessionId = await this._getSession();

      // Delete each key individually
      for (const key of keys) {
        const prefixedKey = this._getPrefixedKey(key);

        try {
          // First find the record
          const searchResult = await this.client.searchRecords({
            content: prefixedKey,
            sessionId,
            strategies: [this.defaultStrategy],
            limit: 1,
          });

          if (searchResult.records && searchResult.records.length > 0) {
            const record = searchResult.records[0];
            await this.client.deleteRecord({
              sessionId,
              recordId: record.record_id,
            });
          }
        } catch (error) {
          console.warn(`Failed to delete key ${key}:`, error);
        }
      }
    } catch (error) {
      throw new Error(`Failed to delete keys: ${error}`);
    }
  }

  /**
   * Yields keys from the TDAI store.
   * @param prefix Optional prefix to filter the keys.
   * @returns An AsyncGenerator that yields keys from the TDAI store.
   */
  async *yieldKeys(prefix?: string): AsyncGenerator<string> {
    try {
      const sessionId = await this._getSession();
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        try {
          const result = await this.client.queryRecords({
            sessionId,
            strategies: [this.defaultStrategy],
            limit: this.yieldKeysScanBatchSize,
            offset,
          });

          if (!result.records || result.records.length === 0) {
            hasMore = false;
            break;
          }

          for (const record of result.records) {
            const parsed = this._parseRecordContent(record.record_content);
            if (parsed) {
              const deprefixedKey = this._getDeprefixedKey(parsed.key);

              if (!prefix || deprefixedKey.startsWith(prefix)) {
                yield deprefixedKey;
              }
            }
          }

          offset += result.records.length;

          // If we got fewer records than requested, we've reached the end
          if (result.records.length < this.yieldKeysScanBatchSize) {
            hasMore = false;
          }
        } catch (error) {
          console.warn(`Error yielding keys at offset ${offset}:`, error);
          hasMore = false;
        }
      }
    } catch (error) {
      console.warn("Error yielding keys:", error);
    }
  }

  /**
   * Close the TDAI client connection
   */
  close(): void {
    this.client.close();
  }
}

